import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import OrangeButton from "../../../../../../components/buttons/OrangeButton";
import Label from "../../../../../../components/form_fields/Label";
import ModalComponent from "../../../../../../components/modals/ModalFormComponent";
import { IAddEditTableForm } from "../../../../../../helpers/appHelpers";
import useEditFormStore from "../../../../../../state-management/useEditFormStore";
import MoneyInput from "../../../../../../components/form_fields/MoneyInput";
import {
	useCreateCompanyCurrencyConfig,
	useUpdateCompanyCurrencyConfig,
} from "../../../../../../services/company-currency.service";
import { CurrencyDto } from "../../../../../../models/currency.dto";
import { useManageTableWithSearchParamsData } from "../../../../../../helpers/useManageTable";
import useCompanyStore from "../../../../../../state-management/useCompanyStore";

interface IFormInputs {
	currencyId: number;
	buyRate: number;
	sellRate: number;
}

interface CompanyCurrencyProps extends IAddEditTableForm {
	currencies: CurrencyDto[];
	reloadTableData?: () => void;
}

const CompanyCurrencyModalForm = ({
	open,
	setOpen,
	title,
	isEditForm,
	tableAPIRef,
	currencies,
	reloadTableData,
}: CompanyCurrencyProps) => {
	const { currentCompany } = useCompanyStore();
	const { formDataValues } = useEditFormStore();
	const [uploading, setUploading] = useState(false);

	const {
		register,
		handleSubmit,
		reset,
		watch,
		setValue,
		formState: { errors },
	} = useForm<IFormInputs>({
		defaultValues: {
			buyRate: isEditForm ? formDataValues?.buyRate : undefined,
			sellRate: isEditForm ? formDataValues?.sellRate : undefined,
		},
	});

	const { mutate: createCompanyCurrency } = useCreateCompanyCurrencyConfig();
	const { mutate: updateCompanyCurrency } = useUpdateCompanyCurrencyConfig();
	const { addNewTableData } = useManageTableWithSearchParamsData();

	const onSubmit: SubmitHandler<IFormInputs> = (data) => {
		setUploading(true);

		if (isEditForm) {
			updateCompanyCurrency(
				{ payload: data, id: formDataValues?.id },
				{
					onSuccess: (updatedData) => {
						try {
							tableAPIRef?.current.updateRows([updatedData.data]);
						} catch (error) {
							console.error("Error updating table rows:", error);
							reloadTableData?.();
						}
						setOpen(false);
						reset();
					},
					onSettled: () => setUploading(false),
				},
			);
		} else {
			createCompanyCurrency(
				{ payload: { ...data, companyId: currentCompany?.id as number } },
				{
					onSuccess: () => {
						addNewTableData();
						setOpen(false);
						reset();
					},
					onSettled: () => setUploading(false),
				},
			);
		}
	};

	const handleClose = () => {
		reset();
		setOpen(false);
	};

	return (
		<ModalComponent
			width={700}
			open={open}
			title={title}
			handleClose={handleClose}
		>
			<form onSubmit={handleSubmit(onSubmit)} className="mt-4">
				<div className="grid grid-cols-2 gap-x-4 gap-y-6">
					{!isEditForm && (
						<>
							<div className="w-full">
								<Label title="Currency" for="currencyId" />
								<select
									className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm  ${
										errors.currencyId
											? " border border-red-500 focus:border-red-500 focus:outline-red-500"
											: "border-none"
									}`}
									{...register("currencyId", {
										required: "Select currency here",
										valueAsNumber: true,
									})}
								>
									<option value="">Select Currency</option>
									{currencies.map((currency) => (
										<option key={currency.id} value={currency.id}>
											{currency.name}
										</option>
									))}
								</select>
								{errors.currencyId && (
									<p className="text-red-500 text-sm mt-1">
										{errors.currencyId.message}
									</p>
								)}
							</div>
						</>
					)}
					<div className="w-full">
						<Label title="Buy Rate" for="buyRate" />
						<MoneyInput
							attributes={{
								id: "buyRate",
								placeholder: "0",
							}}
							register={register}
							name="buyRate"
							setValue={setValue}
							defaultValue={isEditForm ? formDataValues?.buyRate : undefined}
							required="Enter buy rate here"
							error={errors.buyRate}
						/>
						{errors.buyRate && (
							<p className="text-red-500 text-sm mt-1">
								{errors.buyRate.message}
							</p>
						)}
					</div>
					<div className="w-full">
						<Label title="Sell Rate" for="sellRate" />
						<MoneyInput
							attributes={{
								id: "sellRate",
								placeholder: "0",
							}}
							register={register}
							name="sellRate"
							setValue={setValue}
							defaultValue={isEditForm ? formDataValues?.sellRate : undefined}
							required="Enter sell rate here"
							error={errors.sellRate}
						/>
						{errors.sellRate && (
							<p className="text-red-500 text-sm mt-1">
								{errors.sellRate.message}
							</p>
						)}
					</div>
				</div>

				<div className="w-full flex justify-end mt-10">
					<OrangeButton
						title={`${
							uploading
								? "Uploading..."
								: isEditForm
									? "Update Company Currency"
									: "Add New Company Currency"
						}`}
						className={`px-8`}
						type={"submit"}
						disabled={uploading}
					/>
				</div>
			</form>
		</ModalComponent>
	);
};

export default CompanyCurrencyModalForm;
